import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Maximize2, Camera } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

interface GooglePhoto {
  file: string;
  attribution: string | null;
  width: number;
  height: number;
}

interface EntityPhotos {
  handle: string;
  category: string;
  title: string;
  count: number;
  images: GooglePhoto[];
}

interface Manifest {
  photos: EntityPhotos[];
}

interface EntityGalleryProps {
  handle: string;
  title: string;
  fallbackImage?: string;
}

function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function EntityGallery({ handle, title, fallbackImage }: EntityGalleryProps) {
  const { t } = useTranslation();
  const { trackWidget } = useAnalytics();
  const [entity, setEntity] = useState<EntityPhotos | null>(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    fetch('/images/google/manifest.json')
      .then(res => (res.ok ? res.json() : null))
      .then((manifest: Manifest | null) => {
        if (!manifest) return;
        const found = manifest.photos.find(p => p.handle === handle);
        setEntity(found || null);
      })
      .catch(() => setEntity(null))
      .finally(() => setLoading(false));
  }, [handle]);

  const images = entity && entity.count > 0 ? entity.images.map(p => p.file) : fallbackImage ? [fallbackImage] : [];
  const hasMultiple = images.length > 1;

  const prev = useCallback(() => {
    setCurrent(c => (c === 0 ? images.length - 1 : c - 1));
  }, [images.length]);

  const next = useCallback(() => {
    setCurrent(c => (c === images.length - 1 ? 0 : c + 1));
  }, [images.length]);

  const openLightbox = useCallback((index: number) => {
    trackWidget({ widgetType: 'map', action: 'open', resultCount: images.length });
    setLightbox(index);
  }, [images.length, trackWidget]);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  const prevPhoto = useCallback(() => {
    setLightbox(prev => {
      if (prev === null) return prev;
      const n = prev > 0 ? prev - 1 : images.length - 1;
      setCurrent(n);
      return n;
    });
  }, [images.length]);

  const nextPhoto = useCallback(() => {
    setLightbox(prev => {
      if (prev === null) return prev;
      const n = prev < images.length - 1 ? prev + 1 : 0;
      setCurrent(n);
      return n;
    });
  }, [images.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightbox, closeLightbox, prevPhoto, nextPhoto]);

  // Keyboard navigation for carousel
  useEffect(() => {
    if (!hasMultiple || lightbox !== null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasMultiple, lightbox, prev, next]);

  if (loading) {
    return (
      <div className="animate-pulse rounded-2xl bg-sand-100 h-64 sm:h-80 lg:h-96 w-full mb-8">
        <div className="flex items-center justify-center h-full text-sand-400">
          <Camera className="w-6 h-6 mr-2 animate-spin" />
          {t('gallery.loading')}
        </div>
      </div>
    );
  }

  if (images.length === 0) return null;

  const currentImg = images[current];
  const currentPhoto = entity?.images[current];
  const lightboxPhoto = lightbox !== null ? entity?.images[lightbox] : null;

  return (
    <div className="mb-8">
      {/* Hero Carousel */}
      <div className="relative rounded-2xl overflow-hidden group">
        <div className="relative h-64 sm:h-80 lg:h-[28rem]">
          {images.map((img, i) => (
            <button
              key={img + i}
              onClick={() => openLightbox(i)}
              className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${
                i === current ? 'opacity-100' : 'opacity-0'
              } ${hasMultiple ? 'cursor-zoom-in' : 'cursor-default'}`}
              aria-label={t('gallery.openPhoto')
                .replace('{{num}}', String(i + 1))
                .replace('{{title}}', title)}
            >
              <img
                src={img}
                alt={`${title} — foto ${i + 1}`}
                className="w-full h-full object-cover"
                width="1200"
                height="800"
                {...(i > 0 ? { loading: 'lazy', decoding: 'async' } : {})}
              />
            </button>
          ))}

          {/* Zoom hint overlay */}
          {hasMultiple && (
            <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm text-white text-xs font-body px-3 py-1 rounded-full flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <Maximize2 className="w-3 h-3" />
              {current + 1} / {images.length}
            </div>
          )}

          {/* Nav buttons */}
          {hasMultiple && (
            <>
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                aria-label={t('gallery.prevPhoto')}
              >
                <ChevronLeft className="w-5 h-5 text-warm-dark" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                aria-label={t('gallery.nextPhoto')}
              >
                <ChevronRight className="w-5 h-5 text-warm-dark" />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {hasMultiple && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === current ? 'bg-white w-6' : 'bg-white/60 hover:bg-white/80'
                }`}
                aria-label={t('gallery.viewPhoto').replace('{{n}}', String(i + 1))}
              />
            ))}
          </div>
        )}

        {/* Attribution on hero */}
        {currentPhoto?.attribution && (
          <div className="absolute bottom-4 right-4 bg-black/40 backdrop-blur-sm text-white/80 text-[10px] font-body px-2 py-1 rounded max-w-[200px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
            {stripHtml(currentPhoto.attribution)}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                i === current ? 'border-coral-400' : 'border-transparent hover:border-sand-300'
              }`}
              aria-label={t('gallery.viewPhoto').replace('{{n}}', String(i + 1))}
            >
              <img
                src={img}
                alt={`${title} — miniatura ${i + 1}`}
                className="w-full h-full object-cover"
                width="80"
                height="56"
                loading="lazy"
                decoding="async"
              />
            </button>
          ))}
        </div>
      )}

      {/* Google Maps source note */}
      {entity && entity.count > 0 && (
        <p className="text-xs text-sand-400 mt-2 flex items-center gap-1">
          <Camera className="w-3 h-3" />
          {t('gallery.viaGoogleMaps')}
        </p>
      )}

      {/* Lightbox */}
      {lightbox !== null && lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            aria-label={t('gallery.close')}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Prev */}
          {images.length > 1 && (
            <button
              onClick={e => {
                e.stopPropagation();
                prevPhoto();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
              aria-label={t('gallery.prevPhoto')}
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          {/* Next */}
          {images.length > 1 && (
            <button
              onClick={e => {
                e.stopPropagation();
                nextPhoto();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
              aria-label={t('gallery.nextPhoto')}
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          {/* Image */}
          <div
            className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={lightboxPhoto.file}
              alt={`${title} — foto ${lightbox + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              width="1200"
              height="800"
              loading="lazy"
              decoding="async"
            />

            {/* Footer */}
            <div className="mt-3 text-center">
              <p className="text-white/70 text-sm">
                {lightbox + 1} / {images.length}
              </p>
              {lightboxPhoto.attribution && (
                <p
                  className="text-white/50 text-xs mt-1"
                  dangerouslySetInnerHTML={{
                    __html: t('gallery.photoAttribution').replace(
                      '{{attribution}}',
                      lightboxPhoto.attribution
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
